import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../contexts/ApiContext';
import { Brain, Target, Play, CheckCircle, AlertCircle, RefreshCw, Download } from 'lucide-react';

const Mapping = () => {
  const { apiGet, apiPost, bulkAnalyze } = useApi();
  const [mappingStats, setMappingStats] = useState({
    total_controls: 0,
    mapped_controls: 0,
    unmapped_controls: 0,
    coverage_percentage: 0
  });
  const [processing, setProcessing] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [loadingTechniques, setLoadingTechniques] = useState(false);
  const [techniqueLoadStatus, setTechniqueLoadStatus] = useState(null);

  const fetchMappingStats = useCallback(async () => {
    try {
      const controls = await apiGet('/controls');
      const mappedControls = controls.controls?.filter(c => c.technique_id) || [];
      
      setMappingStats({
        total_controls: controls.controls?.length || 0,
        mapped_controls: mappedControls.length,
        unmapped_controls: (controls.controls?.length || 0) - mappedControls.length,
        coverage_percentage: controls.controls?.length > 0 
          ? Math.round((mappedControls.length / controls.controls.length) * 100)
          : 0
      });
    } catch (error) {
      console.error('Failed to fetch mapping stats:', error);
    }
  }, [apiGet]);

  useEffect(() => {
    fetchMappingStats();
  }, [fetchMappingStats]);

  // Check MITRE status and initialize if needed
  const checkMITREStatus = useCallback(async () => {
    try {
      const status = await apiGet('/mitre/status');
      return status;
    } catch (error) {
      console.error('Failed to check MITRE status:', error);
      return { initialized: false, error: error.message };
    }
  }, [apiGet]);

  const handleInitializeMITRE = useCallback(async (force = false) => {
    setLoadingTechniques(true);
    setTechniqueLoadStatus(null);
    
    try {
      // First check status
      const status = await checkMITREStatus();
      
      if (status.initialized && !force && status.days_since_update < 7) {
        setTechniqueLoadStatus({
          success: true,
          message: `MITRE data is current (${status.loaded_techniques} techniques, updated ${status.days_since_update} days ago)`,
          loaded_count: status.loaded_techniques,
          last_update: status.last_update
        });
        return;
      }
      
      // Initialize or update
      const result = await apiPost('/mitre/initialize', { force });
      
      setTechniqueLoadStatus({
        success: true,
        message: result.message,
        loaded_count: result.loaded_techniques,
        total_count: result.total_techniques,
        tactics: result.techniques_by_tactic
      });
    } catch (error) {
      setTechniqueLoadStatus({
        success: false,
        message: error.message
      });
    } finally {
      setLoadingTechniques(false);
    }
  }, [apiPost, checkMITREStatus]);

  // Auto-check on component mount
  useEffect(() => {
    const autoCheckMITRE = async () => {
      const status = await checkMITREStatus();
      if (!status.initialized) {
        // Auto-initialize MITRE data
        await handleInitializeMITRE();
      } else if (status.needs_update) {
        // Show update suggestion
        setTechniqueLoadStatus({
          success: true,
          message: `MITRE data loaded (${status.loaded_techniques} techniques, ${status.days_since_update} days old - consider updating)`,
          loaded_count: status.loaded_techniques,
          needs_update: true
        });
      }
    };
    
    autoCheckMITRE();
  }, [checkMITREStatus, handleInitializeMITRE]);

  const handleBulkMapping = async (batchSize = 10) => {
    setProcessing(true);
    setBulkResults(null);
    
    try {
      const result = await bulkAnalyze({
        org_id: 'securebank-001',
        batch_size: batchSize
      });
      
      setBulkResults(result);
      
      // Refresh stats after processing
      await fetchMappingStats();
      
    } catch (error) {
      setBulkResults({
        success: false,
        error: error.message
      });
    } finally {
      setProcessing(false);
    }
  };

  const downloadMappingReport = () => {
    const reportData = {
      generated_at: new Date().toISOString(),
      stats: mappingStats,
      bulk_results: bulkResults
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `mitre_mapping_report_${Date.now()}.json`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            MITRE ATT&CK Mapping
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            AI-powered mapping of CIS controls to MITRE ATT&CK techniques
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={downloadMappingReport}
            className="btn btn-secondary"
            disabled={!bulkResults}
          >
            <Download className="h-4 w-4 mr-2" />
            Download Report
          </button>
          <button
            onClick={fetchMappingStats}
            className="btn btn-secondary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Stats
          </button>
        </div>
      </div>

      {/* Mapping Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{mappingStats.total_controls}</div>
          <div className="text-sm text-gray-500">Total Controls</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{mappingStats.mapped_controls}</div>
          <div className="text-sm text-gray-500">Mapped Controls</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-orange-600">{mappingStats.unmapped_controls}</div>
          <div className="text-sm text-gray-500">Unmapped Controls</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-600">{mappingStats.coverage_percentage}%</div>
          <div className="text-sm text-gray-500">Coverage Rate</div>
        </div>
      </div>

      {/* Step 1: Load MITRE Techniques */}
      <div className="card">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Brain className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              Step 1: Load MITRE ATT&CK Techniques
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Load 691 MITRE ATT&CK techniques into Vectorize for semantic search and AI analysis.
            </p>
            
            {techniqueLoadStatus && (
              <div className={`mt-3 p-3 rounded-md ${
                techniqueLoadStatus.success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {techniqueLoadStatus.success ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${
                      techniqueLoadStatus.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {techniqueLoadStatus.message}
                      {techniqueLoadStatus.loaded_count && 
                        ` (${techniqueLoadStatus.loaded_count} techniques loaded)`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-4 space-x-2">
              <button
                onClick={() => handleInitializeMITRE(false)}
                disabled={loadingTechniques}
                className="btn btn-primary"
              >
                {loadingTechniques ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Loading Techniques...
                  </>
                ) : (
                  <>
                    <Brain className="h-4 w-4 mr-2" />
                    Initialize MITRE Data
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleInitializeMITRE(true)}
                disabled={loadingTechniques}
                className="btn btn-secondary"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Force Update
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Bulk Mapping */}
      <div className="card">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Target className="h-6 w-6 text-green-600" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              Step 2: AI-Powered Bulk Mapping
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Analyze your CIS controls using Llama 3.1 and semantic search to suggest MITRE ATT&CK technique mappings.
            </p>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => handleBulkMapping(5)}
                disabled={processing || mappingStats.unmapped_controls === 0}
                className="btn btn-primary"
              >
                {processing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Map 5 Controls
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleBulkMapping(10)}
                disabled={processing || mappingStats.unmapped_controls === 0}
                className="btn btn-secondary"
              >
                <Target className="h-4 w-4 mr-2" />
                Map 10 Controls
              </button>
              
              <button
                onClick={() => handleBulkMapping(25)}
                disabled={processing || mappingStats.unmapped_controls === 0}
                className="btn btn-secondary"
              >
                <Target className="h-4 w-4 mr-2" />
                Map 25 Controls
              </button>
            </div>
            
            {mappingStats.unmapped_controls === 0 && (
              <p className="mt-2 text-sm text-green-600">
                ✅ All controls are already mapped to MITRE techniques!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Results */}
      {bulkResults && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Bulk Mapping Results
          </h3>
          
          {bulkResults.success ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {bulkResults.processed_controls}
                  </div>
                  <div className="text-sm text-blue-800">Controls Processed</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {bulkResults.total_mappings}
                  </div>
                  <div className="text-sm text-green-800">Mappings Created</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {bulkResults.total_mappings > 0 
                      ? Math.round((bulkResults.total_mappings / bulkResults.processed_controls) * 100)
                      : 0
                    }%
                  </div>
                  <div className="text-sm text-purple-800">Success Rate</div>
                </div>
              </div>
              
              {bulkResults.results && bulkResults.results.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Control ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Best Technique
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Confidence
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mappings Found
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bulkResults.results.map((result, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {result.control_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {result.best_technique ? (
                              <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                                {result.best_technique}
                              </span>
                            ) : (
                              <span className="text-gray-400">No mapping</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {result.confidence ? (
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                result.confidence > 0.8 ? 'bg-green-100 text-green-800' :
                                result.confidence > 0.6 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {Math.round(result.confidence * 100)}%
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {result.mappings_found || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <p className="text-sm text-gray-600">
                {bulkResults.message}
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">
                    Error: {bulkResults.error}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Brain className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">
              AI-Powered MITRE Mapping Process
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>Load MITRE ATT&CK techniques into Vectorize for semantic search</li>
                <li>Process CIS controls in batches using Llama 3.1 for intelligent analysis</li>
                <li>Generate embeddings for semantic similarity matching</li>
                <li>Store the highest confidence mappings in the database</li>
                <li>View results in the Controls page with MITRE technique assignments</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mapping;